import { useSyncExternalStore, type ReactElement } from "react";
import { flushSync } from "react-dom";
import { createRoot, type Root } from "react-dom/client";
import "../ui/kit/styles.ts";
import "./loading-screen.css";
import "./login-screen.css";
import "./queue/queue-window.css";
import type { KeyboardHolds } from "../ui/input/router.ts";
import { GameStartScreen, type QueueHost } from "./loading-screen.tsx";
import { initialLoadingState, loadingReducer, type LoadingEvent, type LoadingFailure, type LoadingState, type LoadingStepId } from "./loading-state.ts";

/**
 * The loading shell of the game page (P1.4-T5): the loading screen from boot until the world is revealed.
 * The page and the scene tell it what happened (`dispatch`), and it draws the screen only when its state
 * changes, never on the animation frame (ADR-061, "the DOM changes only when a value does"). While it shows,
 * it holds the input router, so no key reaches the game behind it; it steps aside for the login screen
 * (`hide`, `show`), and at the reveal it leaves the page at once, in the same task as the frame that drew the
 * world, and gives the game its keyboard.
 *
 * When the page leaves the world it showed (P1.9-T15: its connection closed, or its sign-in ended), the shell
 * comes back in the same task with the notice that says why, in its failure layout over the whole window, and
 * holds the keyboard again; the notice takes focus to its action as it appears. Reconnect then shows the bar
 * over the steps a rejoin does again until the next reveal.
 *
 * While the page's `hello` waits for room the shell shows the queue window in the bar's place (P1.4-T164), still
 * holding the keyboard; its Leave queue is the page's to act on (`onLeaveQueue`).
 */

/**
 * The shell as the page drives it.
 */
export interface LoadingShell {
  /**
   * Moves the shell by one event.
   *
   * @param event What happened.
   */
  dispatch(event: LoadingEvent): void;
  /**
   * Takes the screen off the page, and its hold off the keyboard, while another surface stands in its
   * place.
   */
  hide(): void;
  /**
   * Puts the screen back and holds the keyboard again, unless the world has been revealed.
   */
  show(): void;
  /**
   * Moves focus to the notice's action while a notice shows, for a surface that stood in front of it and has
   * gone, taking focus with it: the game menu, closing after a confirm the player cancelled once the page had
   * left the world (P1.9-T15).
   */
  focus(): void;
  /**
   * The shell's state.
   *
   * @returns The state.
   */
  readonly state: LoadingState;
}

/**
 * What the shell needs from the page: its steps, the router it holds, what a notice's button does with
 * the failure shown (Retry before the reveal, or the disconnect notice's action after it), and, for the queue
 * window, the character's name as the page knows it and what Leave queue does; a page that gives no `queue` shows
 * the window with no name and a Leave queue that does nothing.
 */
export interface LoadingShellOptions {
  readonly steps: readonly LoadingStepId[];
  readonly holds: KeyboardHolds;
  readonly onRetry: (failure: LoadingFailure) => void;
  readonly queue?: QueueHost;
}

/**
 * Mounts the loading shell in its host and shows it at once.
 *
 * @param host The page's `#loading` element in the overlay root.
 * @param options The steps, the input router and what a notice's button does.
 * @returns The shell.
 * @throws RangeError when the steps are empty or repeat one.
 */
export function mountLoadingShell(host: HTMLElement, options: LoadingShellOptions): LoadingShell {
  let state = initialLoadingState(options.steps);
  let release: (() => void) | null = options.holds.hold();
  const listeners = new Set<() => void>();
  let root: Root | null = createRoot(host);

  /**
   * Adds a listener for the state.
   *
   * @param listener Called when the state changes.
   * @returns A function that removes it.
   */
  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  /**
   * The state React draws.
   *
   * @returns The state.
   */
  const snapshot = (): LoadingState => state;

  /**
   * Hands the failure shown to the page, which acts on its button: Retry and Reconnect clear the notice for
   * the bar first, since the page loads or joins again; Reload and Log in leave it in place while the page
   * reloads.
   */
  const retry = (): void => {
    const failure = state.failure;
    if (failure === null) {
      return;
    }
    if (failure.action === "retry" || failure.action === "reconnect") {
      shell.dispatch({ type: "retried" });
    }
    options.onRetry(failure);
  };

  /**
   * The screen, following the state.
   *
   * @returns The screen.
   */
  function Shell(): ReactElement {
    const current = useSyncExternalStore(subscribe, snapshot);
    return options.queue === undefined ? <GameStartScreen state={current} onRetry={retry} /> : <GameStartScreen state={current} onRetry={retry} queue={options.queue} />;
  }

  /**
   * Takes the screen off the page at the reveal, leaving the host empty, and gives the keyboard back.
   */
  const leave = (): void => {
    host.hidden = true;
    release?.();
    release = null;
    listeners.clear();
    root?.unmount();
    root = null;
  };

  /**
   * Puts the screen back on the page when the page leaves the world it showed, drawn in this task so its
   * notice is there, and focused, before anything else runs, and holds the keyboard again.
   */
  const enter = (): void => {
    const next = createRoot(host);
    root = next;
    host.hidden = false;
    release ??= options.holds.hold();
    flushSync(() => next.render(<Shell />));
  };

  const shell: LoadingShell = {
    dispatch(event: LoadingEvent): void {
      const next = loadingReducer(state, event);
      if (next === state) {
        return;
      }
      state = next;
      if (next.revealed) {
        if (root !== null) {
          leave();
        }
        return;
      }
      if (root === null) {
        enter();
        return;
      }
      for (const listener of listeners) {
        listener();
      }
    },
    hide(): void {
      if (root === null) {
        return;
      }
      host.hidden = true;
      release?.();
      release = null;
    },
    show(): void {
      if (root === null) {
        return;
      }
      host.hidden = false;
      release ??= options.holds.hold();
    },
    focus(): void {
      if (root === null || host.hidden || state.failure === null) {
        return;
      }
      host.querySelector<HTMLElement>("[data-loading='notice'] button")?.focus();
    },
    get state(): LoadingState {
      return state;
    },
  };

  host.hidden = false;
  flushSync(() => root?.render(<Shell />));
  return shell;
}
